import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';

const CARD_EXPENSE_POLICY_DOC = 'financeSettings/cardExpensePolicy';

export type CardExpenseDigestFrequency = 'weekly' | 'daily' | 'off';
export type CardExpenseAutoAssignAction = 'overhead' | 'exclude' | 'not_applicable';

export interface CardExpenseAutoRule {
  id: string;
  label: string;
  vendorContains: string[];
  descriptionContains?: string[];
  action: CardExpenseAutoAssignAction;
  enabled?: boolean;
}

export interface CardExpensePolicy {
  shortTermOpsAssigneeUserId?: string;
  shortTermOpsAssigneeUserName?: string;
  digestFrequency?: CardExpenseDigestFrequency;
  autoAssignRules?: CardExpenseAutoRule[];
  updatedAt?: Date;
}

const DEFAULT_DIGEST_FREQUENCY: CardExpenseDigestFrequency = 'weekly';

export const DEFAULT_CARD_EXPENSE_AUTO_RULES: CardExpenseAutoRule[] = [
  {
    id: 'overhead-intuit-qb',
    label: 'Intuit / QuickBooks subscription → overhead',
    vendorContains: ['intuit', 'quickbooks', 'quick books'],
    action: 'overhead',
    enabled: true,
  },
  {
    id: 'overhead-acodei',
    label: 'Acodei → overhead',
    vendorContains: ['acodei'],
    action: 'overhead',
    enabled: true,
  },
  {
    id: 'overhead-next-insurance',
    label: 'Next Insurance → overhead',
    vendorContains: ['next insurance', 'nextinsurance'],
    action: 'overhead',
    enabled: true,
  },
  {
    id: 'overhead-software-subs',
    label: 'Common software subscriptions → overhead',
    vendorContains: [
      'adobe',
      'google workspace',
      'gsuite',
      'microsoft 365',
      'office 365',
      'dropbox',
      'slack technologies',
      'slack.com',
      'zoom.us',
      'zoom.com',
      'zoom video',
      'notion labs',
      'github',
      'openai',
      'anthropic',
      'cursor',
      'figma',
      'canva',
      'lastpass',
      '1password',
      'norton',
      'malwarebytes',
    ],
    action: 'overhead',
    enabled: true,
  },
  {
    id: 'exclude-owner-draw',
    label: 'Owner draw → excluded / not applicable',
    vendorContains: ['owner draw', 'owner-draw', "owner's draw", 'owners draw', 'draw to owner'],
    descriptionContains: ['owner draw', 'owner-draw', "owner's draw", 'owners draw'],
    action: 'exclude',
    enabled: true,
  },
  {
    id: 'exclude-internal-transfer',
    label: 'Internal transfer → excluded / not applicable',
    vendorContains: [
      'internal transfer',
      'transfer to',
      'transfer from',
      'sequence transfer',
      'pod transfer',
      'xfer',
    ],
    descriptionContains: [
      'internal transfer',
      'transfer between',
      'account transfer',
      'pod transfer',
      'sequence transfer',
    ],
    action: 'not_applicable',
    enabled: true,
  },
];

function parseDigestFrequency(raw: unknown): CardExpenseDigestFrequency {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (s === 'daily' || s === 'off' || s === 'weekly') return s;
  return DEFAULT_DIGEST_FREQUENCY;
}

function parseAutoRules(raw: unknown): CardExpenseAutoRule[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const out: CardExpenseAutoRule[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const id = typeof r.id === 'string' ? r.id.trim() : '';
    const label = typeof r.label === 'string' ? r.label.trim() : '';
    const action = r.action;
    if (!id || !label) continue;
    if (action !== 'overhead' && action !== 'exclude' && action !== 'not_applicable') continue;
    out.push({
      id,
      label,
      vendorContains: Array.isArray(r.vendorContains)
        ? r.vendorContains.map(v => String(v || '').trim()).filter(Boolean)
        : [],
      descriptionContains: Array.isArray(r.descriptionContains)
        ? r.descriptionContains.map(v => String(v || '').trim()).filter(Boolean)
        : undefined,
      action,
      enabled: r.enabled === false ? false : true,
    });
  }
  return out.length > 0 ? out : undefined;
}

export async function getCardExpensePolicy(): Promise<CardExpensePolicy> {
  const snap = await getDoc(doc(db, CARD_EXPENSE_POLICY_DOC));
  if (!snap.exists()) {
    return {
      digestFrequency: DEFAULT_DIGEST_FREQUENCY,
      autoAssignRules: DEFAULT_CARD_EXPENSE_AUTO_RULES,
    };
  }
  const data = snap.data();
  return {
    shortTermOpsAssigneeUserId:
      typeof data.shortTermOpsAssigneeUserId === 'string' ? data.shortTermOpsAssigneeUserId : undefined,
    shortTermOpsAssigneeUserName:
      typeof data.shortTermOpsAssigneeUserName === 'string' ? data.shortTermOpsAssigneeUserName : undefined,
    digestFrequency: parseDigestFrequency(data.digestFrequency),
    autoAssignRules: parseAutoRules(data.autoAssignRules) || DEFAULT_CARD_EXPENSE_AUTO_RULES,
    updatedAt: data.updatedAt?.toDate?.() || undefined,
  };
}

export async function saveCardExpenseDigestFrequency(params: {
  digestFrequency: CardExpenseDigestFrequency;
  updatedBy?: string;
}): Promise<void> {
  await setDoc(
    doc(db, CARD_EXPENSE_POLICY_DOC),
    {
      digestFrequency: params.digestFrequency,
      updatedAt: Timestamp.now(),
      ...(params.updatedBy ? { updatedBy: params.updatedBy } : {}),
    },
    { merge: true },
  );
}

export async function saveCardExpenseAutoAssignRules(params: {
  rules: CardExpenseAutoRule[];
  updatedBy?: string;
}): Promise<void> {
  await setDoc(
    doc(db, CARD_EXPENSE_POLICY_DOC),
    {
      autoAssignRules: params.rules,
      updatedAt: Timestamp.now(),
      ...(params.updatedBy ? { updatedBy: params.updatedBy } : {}),
    },
    { merge: true },
  );
}

export async function saveShortTermOpsAssignee(params: {
  userId: string;
  userName: string;
  updatedBy?: string;
}): Promise<void> {
  const userId = params.userId.trim();
  if (!userId) {
    await setDoc(
      doc(db, CARD_EXPENSE_POLICY_DOC),
      {
        shortTermOpsAssigneeUserId: '',
        shortTermOpsAssigneeUserName: '',
        updatedAt: Timestamp.now(),
        ...(params.updatedBy ? { updatedBy: params.updatedBy } : {}),
      },
      { merge: true },
    );
    return;
  }

  await setDoc(
    doc(db, CARD_EXPENSE_POLICY_DOC),
    {
      shortTermOpsAssigneeUserId: userId,
      shortTermOpsAssigneeUserName: params.userName.trim(),
      updatedAt: Timestamp.now(),
      ...(params.updatedBy ? { updatedBy: params.updatedBy } : {}),
    },
    { merge: true },
  );
}

export async function backfillShortTermOpsAssignee(): Promise<{ updated: number; skipped: number }> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'backfillShortTermOpsAssignee');
  const result = await fn({});
  return result.data as { updated: number; skipped: number };
}
